import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import agent from '../api/axios';
import CheckoutForm from '../components/CheckoutForm';

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/;

const schema = yup.object({
  ownerEmail: yup
    .string()
    .required('Email is required')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email'),
  // keep the field name passwordHash to match backend handling, but validate as plain password
  passwordHash: yup
    .string()
    .required('Password is required')
    .matches(
      passwordRegex,
      'Password must be 12+ chars and include uppercase, lowercase, number and special character'
    ),
  // use undefined (not null) so TypeScript matches the yup types
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('passwordHash'), undefined], 'Passwords must match')
    .required('Confirm password is required'),
  name: yup
    .string()
    .min(3, 'Business name must be at least 3 characters')
    .required('Business name is required'),
  subscriptionId: yup.string().required('Subscription is required'),
});

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [apiError, setApiError] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [subsLoading, setSubsLoading] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState(0);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    trigger,
    watch,
    getValues,
  } = useForm({ resolver: yupResolver(schema), mode: 'onChange' });

  const watchedPassword = watch('passwordHash', '');

  // Load subscriptions
  useEffect(() => {
    let mounted = true;
    setSubsLoading(true);
    agent.Subscriptions.get()
      .then((data) => {
        if (!mounted) return;
        const active = (data ?? []).filter((s: any) => s.active);
        setSubscriptions(active);

        // Check for plan from URL
        const planId = searchParams.get('plan');
        if (
          planId &&
          active.find((s: any) => (s.id ?? s._id ?? s.Id) === planId)
        ) {
          setValue('subscriptionId', planId);
        } else if (active.length > 0) {
          const firstId = active[0].id ?? active[0]._id ?? active[0].Id;
          setValue('subscriptionId', firstId);
        }
        trigger('subscriptionId');
      })
      .catch((err) => {
        console.error('Failed to load subscriptions', err);
        setSubscriptions([]);
      })
      .finally(() => {
        if (mounted) setSubsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [setValue, trigger, searchParams]);

  // Check for return from checkout
  useEffect(() => {
    const businessIdParam = searchParams.get('businessId');
    const sessionId = searchParams.get('session_id');

    if (businessIdParam && sessionId) {
      setBusinessId(businessIdParam);
      setActiveStep(3); // Move to final confirmation step

      let pollCount = 0;
      const maxPolls = 24; // 24 polls * 5 seconds = 2 minutes

      // Check business status
      const checkStatus = () => {
        agent.Business.getStatus(businessIdParam)
          .then((response) => {
            if (response.status === 'active') {
              setRegistrationComplete(true);
              // Redirect to success page
              navigate('/registration/success');
            } else if (response.status === 'pending') {
              pollCount++;

              if (pollCount >= maxPolls) {
                // After 2 minutes, show a message that payment was successful but activation is pending
                setApiError(
                  'Your payment was successful! Account activation is taking longer than expected. You will receive an email once your account is ready.'
                );
                setRegistrationComplete(true);
              }
            }
          })
          .catch((err) => {
            console.error('Failed to check business status', err);
            setApiError(
              'Failed to verify registration status. Please contact support.'
            );
          });
      };

      // Initial check
      checkStatus();

      // Poll every 5 seconds
      const pollInterval = setInterval(checkStatus, 5000);

      // Cleanup
      return () => clearInterval(pollInterval);
    }
  }, [searchParams, navigate]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCheckoutComplete = () => {
    setRegistrationComplete(true);
    setActiveStep(3); // Move to confirmation step
  };

  const handleSubmitRegistration = async (data: any) => {
    // Only proceed with API call if we're on step 1 (subscription selection)
    if (activeStep !== 1) {
      handleNext();
      return;
    }

    try {
      setApiError(null);
      setIsProcessing(true);
      // remove confirmPassword before sending and rename passwordHash to password
      const { confirmPassword, passwordHash, ...rest } = data;
      const payload = {
        ...rest,
        password: passwordHash, // Rename to match backend DTO
      };

      if (!payload.subscriptionId) {
        setApiError('Please select a subscription.');
        setIsProcessing(false);
        return;
      }

      // Call the registration API
      const response = await agent.Business.register(payload);

      // Save client secret, business ID, customer ID, and subscription ID
      setBusinessId(response.businessId);
      setClientSecret(response.clientSecret);
      setCustomerId(response.customerId);
      setSubscriptionId(response.subscriptionId);

      // Move to the checkout step
      handleNext();
      setIsProcessing(false);
    } catch (error: any) {
      console.error(error);
      setApiError(error.response?.data?.message || error.response?.data?.error || 'Registration failed');
      setIsProcessing(false);
    }
  };

  const steps = [
    'Account Information',
    'Select Subscription',
    'Payment',
    'Confirmation',
  ];

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <TextField
              label="Business Name"
              fullWidth
              autoComplete="organization"
              margin="normal"
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
            <TextField
              label="Email"
              fullWidth
              margin="normal"
              autoComplete="email"
              {...register('ownerEmail')}
              error={!!errors.ownerEmail}
              helperText={errors.ownerEmail?.message}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              autoComplete="new-password"
              margin="normal"
              {...register('passwordHash')}
              error={!!errors.passwordHash}
              helperText={
                watchedPassword
                  ? errors.passwordHash?.message
                  : 'At least 12 chars, include uppercase, lowercase, number and special char'
              }
            />
            <TextField
              label="Confirm Password"
              type="password"
              fullWidth
              autoComplete="new-password"
              margin="normal"
              {...register('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
            />
          </>
        );
      case 1:
        return (
          <Controller
            name="subscriptionId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Select Your Subscription Plan
                </Typography>

                {subscriptions.map((s: any) => (
                  <Card
                    key={s.id ?? s._id ?? s.Id}
                    sx={{
                      mb: 2,
                      border:
                        field.value === (s.id ?? s._id ?? s.Id)
                          ? '2px solid #1E23FE'
                          : '1px solid',
                      borderColor:
                        field.value === (s.id ?? s._id ?? s.Id)
                          ? 'primary.main'
                          : 'divider',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 4px 20px rgba(30, 35, 254, 0.15)',
                      },
                    }}
                    onClick={() => {
                      setValue('subscriptionId', s.id ?? s._id ?? s.Id);
                      trigger('subscriptionId');
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {s.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {s.term}
                          </Typography>
                        </Box>
                        <Typography
                          variant="h5"
                          sx={{ color: 'primary.main', fontWeight: 700 }}
                        >
                          ${Number(s.price ?? 0).toFixed(2)}
                        </Typography>
                      </Box>
                      {field.value === (s.id ?? s._id ?? s.Id) && (
                        <Box
                          sx={{
                            mt: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={{ fontWeight: 500 }}
                          >
                            ✓ Selected
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {subsLoading && <CircularProgress size={24} />}

                {subscriptions.length === 0 && !subsLoading && (
                  <Typography color="error">
                    No active subscriptions available
                  </Typography>
                )}
              </Box>
            )}
          />
        );
      case 2: // Payment step with embedded checkout
        return clientSecret && businessId && customerId && subscriptionId ? (
          <CheckoutForm
            clientSecret={clientSecret}
            businessId={businessId}
            customerId={customerId}
            subscriptionId={subscriptionId}
            onComplete={handleCheckoutComplete}
          />
        ) : (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <CircularProgress />
            <Typography>Preparing payment form...</Typography>
          </Box>
        );
      case 3:
        return registrationComplete ? (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Box
              sx={{
                backgroundColor: 'success.light',
                borderRadius: 2,
                padding: 4,
                mb: 3,
              }}
            >
              <Box sx={{ fontSize: '4rem', color: 'success.dark', mb: 2 }}>
                ✓
              </Box>
              <Typography
                variant="h4"
                color="success.dark"
                gutterBottom
                fontWeight="bold"
              >
                Registration Successful!
              </Typography>
              <Typography
                variant="body1"
                sx={{ mt: 2, mb: 1, color: 'success.dark' }}
              >
                Welcome to SimplyPayroll! Your business account has been created
                and your subscription is now active.
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 3, color: 'success.dark', opacity: 0.8 }}
              >
                A confirmation email with your subscription details has been
                sent to your email address.
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: 'background.default',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                padding: 3,
                mb: 3,
                textAlign: 'left',
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ textAlign: 'center', mb: 2 }}
              >
                What's Next?
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <li>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Log in</strong> to your new account
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Set up</strong> your company information and
                    employees
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Process</strong> your first payroll
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Manage</strong> your subscription from your profile
                  </Typography>
                </li>
              </Box>
            </Box>

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/login')}
              sx={{ mt: 2, minWidth: 200 }}
            >
              Proceed to Login
            </Button>
          </Box>
        ) : (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 3 }}>
              Processing your registration...
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Setting up your account and activating your subscription.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This may take a moment. Please don't close this page.
            </Typography>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: (theme) =>
          theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, #f8faf8 0%, #e8f5e9 100%)'
            : 'linear-gradient(135deg, #121212 0%, #1a1a1a 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 3,
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src="/logo192.png"
              alt="SimplyPayroll"
              sx={{
                width: 56,
                height: 56,
              }}
            />
            <Typography variant="h4" align="center" sx={{ fontWeight: 700 }}>
              Create Account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start tracking your 1099 contractor payroll
            </Typography>
          </Box>

          <Card sx={{ p: 2 }}>
            <CardContent>
              <Stepper activeStep={activeStep} sx={{ pb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Steps 0 and 1: Account info and subscription selection */}
              {activeStep < 2 && (
                <form onSubmit={handleSubmit(handleSubmitRegistration)}>
                  {renderStepContent(activeStep)}

                  {apiError && (
                    <Box mt={2} textAlign="center">
                      <Typography variant="body2" color="error">
                        {apiError}
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 3,
                    }}
                  >
                    {activeStep > 0 && activeStep < 2 && (
                      <Button disabled={isProcessing} onClick={handleBack}>
                        Back
                      </Button>
                    )}
                    {activeStep === 0 && (
                      <Button onClick={() => navigate('/')}>Cancel</Button>
                    )}

                    <div>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={
                          isProcessing ||
                          (activeStep === 0 &&
                            (!getValues('name') ||
                              !getValues('ownerEmail') ||
                              !getValues('passwordHash') ||
                              !getValues('confirmPassword') ||
                              !!errors.name ||
                              !!errors.ownerEmail ||
                              !!errors.passwordHash ||
                              !!errors.confirmPassword)) ||
                          (activeStep === 1 &&
                            (!getValues('subscriptionId') ||
                              subscriptions.length === 0))
                        }
                      >
                        {isProcessing ? (
                          <CircularProgress size={24} />
                        ) : activeStep === 1 ? (
                          'Continue to Payment'
                        ) : (
                          'Next'
                        )}
                      </Button>
                    </div>
                  </Box>
                </form>
              )}

              {/* Step 2: Payment form (outside parent form to avoid nesting) */}
              {activeStep === 2 && (
                <>
                  {renderStepContent(activeStep)}
                  {apiError && (
                    <Box mt={2} textAlign="center">
                      <Typography variant="body2" color="error">
                        {apiError}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* Step 3: Confirmation */}
              {activeStep === 3 && renderStepContent(activeStep)}
            </CardContent>
          </Card>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Box
                component="a"
                href="/login"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              >
                Sign in
              </Box>
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
